import React, { useState, useEffect, forwardRef } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomModal from "../CustomModal";
import Swal from "sweetalert2";
import { stream_data } from "../../config/stream_data";

const CustomDateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <div
    className="form-control d-flex align-items-center justify-content-between"
    onClick={onClick}
    ref={ref}
    style={{
      minHeight: "42px",
      minWidth: "125px",
      borderColor: "#d1d5db",
      borderRadius: "4px",
      borderWidth: "1px",
      borderStyle: "solid",
      backgroundColor: "white",
      cursor: "pointer",
      padding: "2px 8px",
      transition: "all 0.2s",
      width: '100%'
    }}
    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#36348e")}
    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#d1d5db")}
  >
    <span className={value ? "text-dark" : "text-muted"}>
      {value || placeholder}
    </span>
    <div className="text-muted d-flex align-items-center">
      <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="css-8mmkcg" style={{ fill: '#ccc' }}>
        <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
      </svg>
    </div>
  </div>
));

function EnrollStudentInProgramModal({ show, onClose, programId }) {
  const [selectedDegrees, setSelectedDegrees] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedStudyingYears, setSelectedStudyingYears] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [showStudentList, setShowStudentList] = useState(false);

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_LMS;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;
  const INSTITUTE_ID = sessionStorage.getItem("instituteId");

  // Prepare degree options from stream_data
  const degreeOptions = [];
  stream_data.streams.forEach((stream) => {
    stream.degrees.forEach((degree) => {
      degreeOptions.push({
        value: degree.name,
        label: degree.name,
      });
    });
  });

  // Prepare year options


  // Prepare studying year options
  const studyingYearOptions = stream_data.studying_years.map((year) => ({
    value: year,
    label: year,
  }));

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedDegrees([]);
      setSelectedYear(null);
      setSelectedStudyingYears([]);
      setStudents([]);
      setSelectedStudents([]);
      setShowStudentList(false);
    }
  }, [show]);

  // Handle Get Students
  const handleGetStudents = async () => {
    if (!selectedDegrees.length || !selectedYear || !selectedStudyingYears.length) {
      Swal.fire({
        icon: "warning",
        title: "Missing Filters",
        text: "Please select at least one Degree, Year, and Studying Year",
        confirmButtonColor: "#36348e",
      });
      return;
    }

    setIsLoadingStudents(true);
    setShowStudentList(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/students-by-institute`, {
        method: "POST",
        headers: {
          Authorization: API_TOKEN,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          institute_id: [parseInt(INSTITUTE_ID)],
          degree_title: selectedDegrees.map((d) => d.value),
          year: [selectedYear.getFullYear()],
          studying_year: selectedStudyingYears.map((y) => y.value),
        }),
      });

      const result = await response.json();

      if (response.ok && result.data && Array.isArray(result.data)) {
        setStudents(result.data);
        setSelectedStudents(result.data.map((student) => student.id));
        setShowStudentList(true);
      } else {
        Swal.fire({
          icon: "info",
          title: "No Students Found",
          text: result.message || "No students found matching the criteria",
          confirmButtonColor: "#36348e",
        });
        setStudents([]);
        setSelectedStudents([]);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch students. Please try again.",
        confirmButtonColor: "#36348e",
      });
    } finally {
      setIsLoadingStudents(false);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedStudents(students.map((student) => student.id));
    } else {
      setSelectedStudents([]);
    }
  };

  // Handle individual student checkbox
  const handleStudentSelect = (studentId) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter((id) => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  // Handle Enroll Students
  const handleEnrollStudents = async () => {
    if (selectedStudents.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Students Selected",
        text: "Please select at least one student to enroll",
        confirmButtonColor: "#36348e",
      });
      return;
    }

    Swal.fire({
      title: "Enrolling Students...",
      html: "Please wait while we enroll the selected students",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/assigned-program-to-student`,
        {
          method: "POST",
          headers: {
            Authorization: API_TOKEN,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            program_ids: [programId],
            student_ids: selectedStudents,
            institute_id: parseInt(INSTITUTE_ID),
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: result.message || "Students enrolled successfully",
          confirmButtonColor: "#36348e",
        }).then(() => {
          onClose();
          window.location.reload();
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Enrollment Failed",
          text: result.message || "Failed to enroll students. Please try again.",
          confirmButtonColor: "#36348e",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Failed to connect to the server. Please try again.",
        confirmButtonColor: "#36348e",
      });
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "42px",
      borderColor: "#d1d5db",
      "&:hover": {
        borderColor: "#36348e",
      },
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#36348e"
        : state.isFocused
          ? "#f3f4f6"
          : "white",
      color: state.isSelected ? "white" : "#374151",
      "&:hover": {
        backgroundColor: state.isSelected ? "#36348e" : "#e5e7eb",
      },
    }),
  };

  // Close menu on outside click only for multi-selects
  const closeMenuOnSelect = false;

  return (
    <CustomModal
      show={show}
      onClose={onClose}
      title="Enroll Students in Program"
      width={window.innerWidth < 768 ? "95%" : window.innerWidth < 992 ? "90%" : 900}
    >
      <div className="mt-4">
        {/* Filters Section */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label fw-semibold text-dark">
              Degree Title <span className="text-danger">*</span>
            </label>
            <Select
              isMulti
              value={selectedDegrees}
              onChange={setSelectedDegrees}
              options={degreeOptions}
              placeholder="Select Degree"
              styles={customSelectStyles}
              closeMenuOnSelect={closeMenuOnSelect}
            />
          </div>

          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label fw-semibold text-dark">
              Year <span className="text-danger">*</span>
            </label>
            <div className="custom-date-picker-wrapper">
              <DatePicker
                selected={selectedYear}
                onChange={(date) => setSelectedYear(date)}
                showYearPicker
                dateFormat="yyyy"
                placeholderText="Select Year"
                className="form-control"
                wrapperClassName="w-100"
                customInput={<CustomDateInput />}
                isClearable
              />
            </div>
          </div>

          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label fw-semibold text-dark">
              Studying Year <span className="text-danger">*</span>
            </label>
            <Select
              isMulti
              value={selectedStudyingYears}
              onChange={setSelectedStudyingYears}
              options={studyingYearOptions}
              placeholder="Select Studying Year"
              styles={customSelectStyles}
              closeMenuOnSelect={closeMenuOnSelect}
            />
          </div>

          <div className="col-12 col-sm-6 col-md-3 d-flex align-items-end">
            <button
              onClick={handleGetStudents}
              disabled={isLoadingStudents}
              className="btn btn-primary px-4 py-2 fw-semibold w-100"
            >
              {isLoadingStudents ? (
                <>
                  <i className="bi bi-arrow-clockwise me-2 spinner-border spinner-border-sm"></i>
                  Loading...
                </>
              ) : (
                <>
                  <i className="bi bi-search me-2"></i>
                  Get Students
                </>
              )}
            </button>
          </div>
        </div>

        {/* Students Table */}
        {showStudentList && students.length > 0 && (
          <div>
            <div className="border rounded mb-3 overflow-auto" style={{ maxHeight: "clamp(200px, 40vh, 400px)" }}>
              <table className="table table-hover mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th style={{ width: "50px" }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedStudents.length === students.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => handleStudentSelect(student.id)}
                        />
                      </td>
                      <td className="text-muted">{student.id}</td>
                      <td className="fw-medium">{student.first_name}</td>
                      <td className="text-muted">{student.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Selected Count */}
            {/* Mobile: stacked, Desktop: side-by-side */}
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 gap-sm-3">
              {/* Selected count on its own row on mobile, inline on desktop */}
              <div className="fw-medium w-100 w-sm-auto text-start">
                {selectedStudents.length} of {students.length} student(s) selected
              </div>
              {/* Buttons always in one row */}
              <div className="d-flex flex-row gap-2 w-100 w-sm-auto justify-content-end">
                <button
                  onClick={onClose}
                  className="btn btn-outline-primary px-4 py-2 fw-semibold flex-fill flex-sm-grow-0"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEnrollStudents}
                  className="btn btn-primary px-4 py-2 fw-semibold shadow-sm flex-fill flex-sm-grow-0"
                >
                  Enroll Students
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No Students Message */}
        {showStudentList && students.length === 0 && (
          <div className="text-center text-muted py-5">
            <i className="bi bi-inbox d-block mb-3" style={{ fontSize: window.innerWidth < 576 ? "36px" : "48px" }}></i>
            <p className="fs-6 mb-0">
              No students found matching the selected criteria
            </p>
          </div>
        )}
      </div>
    </CustomModal>
  );
}

export default EnrollStudentInProgramModal;